/**
 * Phone numbers.
 *
 * The mobile number IS the login identity for this club, so it has to be
 * stored one way and one way only. SPEC.md §4: normalise to E.164 on write.
 *
 * Members will type their number every way a number can be typed —
 * `8197091531`, `08197091531`, `+91 81970 91531`, `91-8197091531`. All of
 * those are the same person, and the roster lookup has to agree.
 */

/** India. The club is in Tirupur; assume +91 when no country code is given. */
const DEFAULT_COUNTRY_CODE = '91';
const NATIONAL_LENGTH = 10;

/**
 * Normalises anything a member might type into E.164 (`+918197091531`).
 * Returns null when it cannot be a valid Indian mobile number, so the caller
 * shows a validation message rather than silently searching for nonsense.
 */
export function normalizePhone(input) {
  if (!input) return null;

  const hadPlus = input.trim().startsWith('+');
  let digits = input.replace(/\D/g, '');
  if (!digits) return null;

  if (hadPlus) {
    // Already international. Trust the country code as typed.
    return digits.length >= 11 && digits.length <= 15 ? `+${digits}` : null;
  }

  // Trunk prefix, as in 08197091531.
  digits = digits.replace(/^0+/, '');

  if (digits.length === NATIONAL_LENGTH) {
    return `+${DEFAULT_COUNTRY_CODE}${digits}`;
  }

  // Already carries the country code but no plus, as in 918197091531.
  if (
    digits.length === DEFAULT_COUNTRY_CODE.length + NATIONAL_LENGTH &&
    digits.startsWith(DEFAULT_COUNTRY_CODE)
  ) {
    return `+${digits}`;
  }

  // Some other country, entered without a plus.
  if (digits.length > NATIONAL_LENGTH && digits.length <= 15) {
    return `+${digits}`;
  }

  return null;
}

/** Two numbers are the same person if they normalise to the same E.164 string. */
export function samePhone(a, b) {
  const left = normalizePhone(a);
  const right = normalizePhone(b);
  return left !== null && left === right;
}

/**
 * The member behind a number, preferring one who is still on the roster.
 *
 * The preference matters. A member who left and later rejoined can have two
 * records; matching the first one in array order handed back the OLD, `left`
 * record and locked them out of the membership they had just paid for. The
 * live record always wins; a `left` one is only returned when it is all there
 * is, so sign-in can still say "that membership is no longer active".
 */
export function findMemberByPhone(members, phone) {
  const e164 = normalizePhone(phone);
  if (!e164) return null;
  const matches = members.filter((m) => normalizePhone(m.phone) === e164);
  return matches.find((m) => m.status !== 'left') ?? matches[0] ?? null;
}

/**
 * Whether a number already belongs to someone on the live roster.
 * `exceptId` lets an edit form leave a member's own number alone.
 */
export function phoneTaken(members, phone, exceptId = null) {
  const found = findMemberByPhone(members, phone);
  return Boolean(found && found.status !== 'left' && found.id !== exceptId);
}

/** `+918197091531` → `+91 81970 91531`, for display only. Never for storage. */
export function formatPhone(phone) {
  const e164 = normalizePhone(phone);
  if (!e164) return phone ?? '—';

  if (e164.startsWith(`+${DEFAULT_COUNTRY_CODE}`) && e164.length === 13) {
    const national = e164.slice(3);
    return `+${DEFAULT_COUNTRY_CODE} ${national.slice(0, 5)} ${national.slice(5)}`;
  }
  return e164;
}

/**
 * The last two digits, for "we sent a code to the number ending 31".
 * Confirms they typed the right number without printing it on a shared screen.
 */
export function phoneHint(phone) {
  const e164 = normalizePhone(phone);
  return e164 ? e164.slice(-2) : '••';
}
